import { Injectable } from '@angular/core';
import { take, switchMap, map } from 'rxjs/operators';
import { ModalController } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { lastValueFrom, Subject, of, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { InputAppClientDBFacade, AppMasterDBFacade, ToastNotificationService } from '@monorepo/core-data';
import { MeasurementQcDataProvider, ScannerDataFormatter, AqlCommonService } from '@monorepo/services';
import { CpiHelper, InputAppAPIHelperUtil, BCTXComputationHelper } from '@monorepo/utils';
import {
  lastMeasuredTagData,
  lastFetchedTagData,
  getBctxDetailsByBctxKey,
  selectLastMeasuredBctxDetails,
  selectLastFetchedBctxDetails,
} from './measurement-qc.query';
import {
  InitializeMeasurementQcDB,
  IncreasePiecesMeasuredCountbySizeAndBctxKey,
  UpdateBctxMap,
} from './measurement-qc.actions';
import { ConfirmationModalComponent } from '@monorepo/ui';
import {
  TMeasurementQcDB,
  TFetchBctxDetailsByPerPieceTagIdRequest,
  TBCTXKeysWithoutSize,
  TCurrentScannedTagDetails,
  TTransformedFetchBctxDetailsByPerPieceTagIdResponse,
  TSavePerPieceTagMeasurementQCRequest,
  TSavePerPieceTagMeasurementQCResponse,
  TBctxItemWithoutSize,
  TBctxHeaderItem,
  TMeasurementQC,
  TMqcBctx,
  TAddOrUpdateThresholdRequest,
  TSizeMeasurementQc,
  TMqcSamples,
  TMeasurementQcTransactionLogResponse,
  TUndoPerPieceMeasurementResponse,
  TMeasurementQcTransactionLogRequest,
  TUserPaginationOption,
  TBctxItem,
} from '@monorepo/types';

@Injectable({
  providedIn: 'root',
})
export class MeasurementQcDBFacade {
  private initCompleted = new Subject<void>();

  constructor(
    private store: Store<TMeasurementQcDB>,
    private apiHelper: InputAppAPIHelperUtil,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private mQcDataProvider: MeasurementQcDataProvider,
    private appMasterDBFacade: AppMasterDBFacade,
    private translate: TranslateService,
    private toastService: ToastNotificationService,
    private modalCtrl: ModalController,
    private scannerDataFormatter: ScannerDataFormatter,
    private aqlCommonService: AqlCommonService,
  ) {}

  initializeMeasurementQcDB() {
    this.store.dispatch(new InitializeMeasurementQcDB());
  }

  validateTagType(tagId: string): boolean {
    return CpiHelper.getTagType(tagId.charAt(0)) === 'PER_PIECE';
  }

  measurementQcDBInitCompleted() {
    this.initCompleted.next();
  }

  calculateSamples(bctxDetails: TTransformedFetchBctxDetailsByPerPieceTagIdResponse, tagSize: string): TMqcSamples {
    const totalSamples = bctxDetails.sizesMeasurementQC.find((item) => item.size === tagSize)?.threshold || 0;
    const samplesMeasured = bctxDetails.sizesMeasurementQC.find((item) => item.size === tagSize)?.piecesMeasured || 0;
    const samplesRemaining = Math.max(totalSamples - samplesMeasured, 0);
    return { totalSamples, samplesRemaining };
  }

  async mqcInputExitModal(message: string, tagSize: string) {
    if (tagSize !== 'empty') {
      this.toastService.showNofification({
        message: this.translate.instant(message, { size: tagSize }),
        color: 'danger',
        duration: 3000,
      });
      return 'EXIT';
    }
    const exitModal = await this.modalCtrl.create({
      component: ConfirmationModalComponent,
      componentProps: {
        header: 'mqc_app.mqc_input.are_you_sure_exit',
        contentTxts: ['mqc_app.mqc_input.once_you_exit'],
        closeModalBtnTxt: 'mqc_app.mqc_input.cancel',
        primaryBtnTxt: 'mqc_app.mqc_input.yes_exit',
      },
      cssClass: 'm-modal_confirmation',
    });
    await exitModal.present();
    const dismissEmitValue = await exitModal.onDidDismiss<string>();
    const role = dismissEmitValue.role === 'SAVE' ? 'EXIT' : '';
    return role;
  }

  getThresholdHitErrorProps() {
    const props = {
      header: 'mqc_app.landing.alert',
      contentTxts: ['mqc_app.landing.threshold_hit', 'mqc_app.landing.are_you_sure_to_continue'],
      closeModalBtnTxt: 'mqc_app.landing.cancel',
      primaryBtnTxt: 'mqc_app.landing.update_threshold',
      headerColor: 'red',
    };
    return props;
  }

  async decodeScannerDataAndAddEvent(
    byteString: string,
  ): Promise<{ tadId: string; displayTagId: string; bctxItems: TBctxItem[] } | null> {
    const formattedTagData = this.scannerDataFormatter.decodeTagString(byteString);
    console.log('Formatted Tag Data: ', formattedTagData);
    const displayTagId = formattedTagData.data['did'];
    const tadId = formattedTagData.data['id'];
    if (!this.validateTagType(displayTagId)) {
      this.apiHelper.showPopup(
        this.translate.instant('common.error'),
        this.translate.instant('mqc_app.landing.not_per_piece_tag'),
      );
      return null;
    }
    if (!formattedTagData.status || !displayTagId || !tadId) {
      this.apiHelper.showPopup(
        this.translate.instant('common.error'),
        formattedTagData.error ? formattedTagData.error : this.translate.instant('common.scanError'),
      );
      return null;
    }
    const bctxItems = await lastValueFrom(
      this.inputAppClientDBFacade.getAllKvArray(formattedTagData.data).pipe(take(1)),
    );
    if (bctxItems.length > 0) {
      return { tadId, displayTagId, bctxItems };
    } else {
      this.apiHelper.showPopup(
        this.translate.instant('common.error'),
        this.translate.instant('common.tagMappingError'),
      );
      return null;
    }
  }

  increaseProgressBasedOnSizeAndBctxKey(bctxKey: string, tagSize: string) {
    this.store.dispatch(new IncreasePiecesMeasuredCountbySizeAndBctxKey(bctxKey, tagSize));
  }

  getMeasurementQcDBInitCompletionStatus() {
    return this.initCompleted.asObservable();
  }

  getLastMeasuredTagData() {
    return this.store.pipe(select(lastMeasuredTagData));
  }

  getLastFetchedTagData() {
    return this.store.pipe(select(lastFetchedTagData));
  }

  getLastMeasuredBctxDetails() {
    return this.store.pipe(select(selectLastMeasuredBctxDetails));
  }

  getLandingPageBctxEmptyData(): TBctxHeaderItem[] {
    return this.getLandingPageBctxHeaderData(this.getMqcLandingPageMockData());
  }

  getMqcLandingPageMockData(): TTransformedFetchBctxDetailsByPerPieceTagIdResponse {
    const mqcLandingPageMockData: TTransformedFetchBctxDetailsByPerPieceTagIdResponse = {
      bctxDetails: {
        buyer: '--',
        po: '--',
        style: '--',
        color: '--',
        erp: '--',
        product_type: '--',
        product_category: '--',
        order_ref: '--',
      },
      error: null,
      factoryId: '--',
      status: 'FAILURE',
      updatedBy: 'SRIRAM',
      updatedAt: 1234567890,
      currentScannedTagDetails: {
        perPieceTagDisplayId: '',
        perPieceTagId: '',
        size: '--',
      },
      sizesMeasurementQC: [],
    };
    return mqcLandingPageMockData;
  }

  getLandingPageBctxHeaderData(bctxDetails: TTransformedFetchBctxDetailsByPerPieceTagIdResponse): TBctxHeaderItem[] {
    const headerData: TBctxHeaderItem[] = [];
    const { buyer, style, po, color } = bctxDetails.bctxDetails;
    headerData.push(
      { label: 'Buyer', value: buyer || '--' },
      { label: 'PO', value: po || '--' },
      { label: 'Style', value: style || '--' },
      { label: 'Color', value: color || '--' },
      { label: 'Size', value: bctxDetails.sizesMeasurementQC.map((item) => item.size).join(',') || '--' },
      { label: 'Tag ID', value: `#${bctxDetails.currentScannedTagDetails.perPieceTagDisplayId}` || '--' },
    );
    return headerData;
  }

  getMqcInputBctxHeaderData(
    bctxDetails: TTransformedFetchBctxDetailsByPerPieceTagIdResponse,
    selectedLine: string,
  ): TBctxHeaderItem[] {
    const headerData: TBctxHeaderItem[] = [];
    const { buyer, po, style, color, product_type } = bctxDetails.bctxDetails;
    headerData.push(
      { label: 'Line', value: selectedLine || '--' },
      { label: 'Buyer', value: buyer || '--' },
      { label: 'PO', value: po || '--' },
      { label: 'Style', value: style || '--' },
      { label: 'Color', value: color || '--' },
      { label: 'Product Type', value: product_type || '--' },
      { label: 'Size', value: bctxDetails.currentScannedTagDetails.size || '--' },
      { label: 'Tag ID', value: `#${bctxDetails.currentScannedTagDetails.perPieceTagDisplayId}` || '--' },
    );
    return headerData;
  }

  getMeasurementIssuesForMeasurementQc(
    bctxItems: TMqcBctx,
    orderKeys: TBCTXKeysWithoutSize[],
    size: string,
    subject_key: string,
    department: string,
    domRole: string,
  ) {
    const orderBctxItems = this.makeOrderedBctxWithoutSize(orderKeys, bctxItems);
    const payload = orderBctxItems.reduce((acc, curr) => {
      acc[curr.k] = curr.v;
      return acc;
    }, {} as { [key: string]: string });
    payload['size'] = size;
    payload['subject_key'] = subject_key;
    payload['department'] = department;
    if (department !== 'COMMON') payload['domRole'] = domRole; // Include domRole only if department is not COMMON

    return this.mQcDataProvider.getMeasurementPointsBasedOnSize(payload);
  }

  getBctxDetailsByBctxKeyAndMeasurementIssues(bctxKey: string) {
    return this.getBctxDetailsByBctxKey(bctxKey).pipe(
      switchMap((bctxDetails) => {
        if (!bctxDetails) {
          return of({ bctxDetails: null, measurementIssues: [], selectedLine: '' });
        }
        return this.appMasterDBFacade.getConfigurations().pipe(
          take(1),
          switchMap((config) => {
            return this.inputAppClientDBFacade.getBctxItemsOrderWithoutSize().pipe(
              take(1),
              switchMap((orderKeys) => {
                return this.appMasterDBFacade.getSelectedStation().pipe(
                  take(1),
                  switchMap((station) => {
                    return this.appMasterDBFacade.getSubjectKey().pipe(
                      take(1),
                      switchMap((subjectKey) => {
                        const domRole = station?.gatek ? station.gatek : '';
                        const selectedLine = station?.dis_name ? station.dis_name : '';
                        const measurement_qc_department_id = config.measurementQcDepartmentId;
                        return this.getMeasurementIssuesForMeasurementQc(
                          bctxDetails.bctxDetails,
                          orderKeys,
                          bctxDetails.currentScannedTagDetails.size,
                          subjectKey,
                          measurement_qc_department_id,
                          domRole,
                        ).pipe(
                          map((measurementIssues) => ({
                            bctxDetails,
                            measurementIssues,
                            selectedLine,
                          })),
                        );
                      }),
                    );
                  }),
                );
              }),
            );
          }),
        );
      }),
    );
  }

  fetchBundleTransactionLog(
    filters: TMeasurementQcTransactionLogRequest['selectedFilters'],
    userPaginationOption: TUserPaginationOption,
  ): Observable<TMeasurementQcTransactionLogResponse> {
    return this.appMasterDBFacade.getAppDetailsCPI().pipe(
      take(1),
      map((appDetails) => [appDetails] as const),
      switchMap(([appDetails]) => {
        return this.mQcDataProvider.fetchMeasurementQcTransactionLog({
          appDetails: appDetails,
          selectedFilters: filters,
          factoryId: appDetails.factoryId,
          pageNo: userPaginationOption.pageNo,
          pageSize: userPaginationOption.pageSize,
        });
      }),
    );
  }

  undoPerPieceMeasurement(
    perPieceTagDisplayId: string,
    order_item_id: string,
  ): Observable<TUndoPerPieceMeasurementResponse> {
    return this.appMasterDBFacade.getAppDetailsCPI().pipe(
      take(1),
      map((appDetails) => [appDetails] as const),
      switchMap(([appDetails]) => {
        return this.mQcDataProvider.undoPerPieceMeasurement({
          appDetails: appDetails,
          factoryId: appDetails.factoryId,
          perPieceTagDisplayId,
          order_item_id,
        });
      }),
    );
  }

  async updateMqcThresholdOfBctx(mqcBctx: TMqcBctx, sizesMeasurementQC: TSizeMeasurementQc[]) {
    const appDetails = await lastValueFrom(this.appMasterDBFacade.getAppDetailsCPI().pipe(take(1)));
    const payLoad: TAddOrUpdateThresholdRequest = {
      appDetails,
      ...mqcBctx,
      factoryId: appDetails.factoryId,
      sizesMeasurementQC,
    };
    const apiResponse = await lastValueFrom(this.mQcDataProvider.updateMqcThresholdOfBctx(payLoad));
    return apiResponse;
  }

  getLastFetchedBctxDetails() {
    return this.store.pipe(select(selectLastFetchedBctxDetails));
  }

  getBctxDetailsByBctxKey(bctxKey: string) {
    return this.store.pipe(select(getBctxDetailsByBctxKey(bctxKey)));
  }

  makeOrderedBctxWithoutSize(orderKeys: TBCTXKeysWithoutSize[], bctxResponse: TMqcBctx): TBctxItemWithoutSize[] {
    const val = orderKeys.map((key) => {
      const value = key in bctxResponse ? bctxResponse[key as keyof TMqcBctx] : '';
      return {
        k: key,
        v: value ?? '',
      };
    });
    return val;
  }

  updatePerPieceTagIdForExistingBctxKeyOnScan(
    bctxKey: string,
    updatedData: TTransformedFetchBctxDetailsByPerPieceTagIdResponse,
  ) {
    this.store.dispatch(new UpdateBctxMap(bctxKey, updatedData, false));
  }

  updateBctxDetailsAndLastFetchedTag(
    bctxKey: string,
    bctxResponse: TTransformedFetchBctxDetailsByPerPieceTagIdResponse,
  ) {
    this.store.dispatch(new UpdateBctxMap(bctxKey, bctxResponse, true));
  }

  async savePerPieceTagMeasurements(
    order_item_id: string,
    measurement_qc: TMeasurementQC[],
    bctxDetails: TMqcBctx,
    currentScannedTagDetails: TCurrentScannedTagDetails,
  ): Promise<TSavePerPieceTagMeasurementQCResponse | null> {
    await this.apiHelper.presentLoading(this.translate.instant('mqc_app.mqc_input.saving_measurements'));
    try {
      const appDetails = await lastValueFrom(this.appMasterDBFacade.getAppDetailsCPI().pipe(take(1)));
      const factoryId = appDetails.factoryId;
      const payload: TSavePerPieceTagMeasurementQCRequest = {
        ...bctxDetails,
        ...currentScannedTagDetails,
        order_item_id,
        auditId: this.aqlCommonService.create_UUID(),
        measurement_qc,
        appDetails,
        factoryId,
      };
      const apiResponse = await lastValueFrom(this.mQcDataProvider.savePerPieceTagMeasurements(payload).pipe(take(1)));
      await this.apiHelper.dismissLoading();
      return apiResponse;
    } catch {
      await this.apiHelper.dismissLoading();
      return null;
    }
  }

  async fetchBctxDetailsByPerPieceTagIdAndUpdateDB(
    perPieceTagDisplayId: string,
    perPieceTagId: string,
  ): Promise<TTransformedFetchBctxDetailsByPerPieceTagIdResponse | null> {
    await this.apiHelper.presentLoading(this.translate.instant('mqc_app.landing.fetching_bctx_details'));
    try {
      const appDetails = await lastValueFrom(this.appMasterDBFacade.getAppDetailsCPI().pipe(take(1)));
      const factoryId = appDetails.factoryId;

      const payload: TFetchBctxDetailsByPerPieceTagIdRequest = {
        perPieceTagDisplayId,
        perPieceTagId,
        factoryId,
        appDetails,
      };

      const bctxResponse = await lastValueFrom(this.mQcDataProvider.getBctxDetailsByTagId(payload).pipe(take(1)));
      if (
        bctxResponse.status === 'SUCCESS' &&
        bctxResponse?.sizesMeasurementQC.find((item) => item.size === bctxResponse.currentScannedTagDetails.size)
      ) {
        const orderKeys: TBCTXKeysWithoutSize[] = await lastValueFrom(
          this.inputAppClientDBFacade.getBctxItemsOrderWithoutSize().pipe(take(1)),
        );
        const bctxItems: TBctxItemWithoutSize[] = this.makeOrderedBctxWithoutSize(orderKeys, bctxResponse.bctxDetails);
        const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(orderKeys, bctxItems);

        this.updateBctxDetailsAndLastFetchedTag(bctxKey, bctxResponse);
        await this.apiHelper.dismissLoading();
        return bctxResponse;
      } else {
        await this.apiHelper.dismissLoading();
        this.apiHelper.showPopup(
          this.translate.instant('mqc_app.common.error'),
          bctxResponse?.error || this.translate.instant('mqc_app.landing.tag_not_found'),
        );
        return null;
      }
    } catch (error) {
      await this.apiHelper.dismissLoading();
      this.apiHelper.showPopup(
        this.translate.instant('mqc_app.common.error'),
        this.translate.instant('mqc_app.common.something_went_wrong'),
      );
      return null;
    }
  }
}
